import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import _get from 'lodash/get';

import moment from 'moment';
import qs from 'qs';

import checkIsExcludedCountryCode from '../shared/checkIsExcludedCountryCode';
import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';

import { default as createCalendar } from '../shared/calendar';
import createFilters from '../shared/filters';

const isRocket = process.env.CPA_ENV === 'rocket';

const calendarOfCreationDate = createCalendar({
	namespace: 'conversionsGarantApproveRequests',
	stateNodeName: 'calendarOfCreationDate',
	defaultValue: {
		start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// actions types
const FETCH_DATA_SUCCESS = 'conversions-garant-approve-requests/fetch-data-success';
const FETCH_DATA_ERROR = 'conversions-garant-approve-requests/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'conversions-garant-approve-requests/toggle-progress-bar';
const SET_MONTHLY_PAYMENTS = 'conversions-garant-approve-requests/set-monthly-payments';

const LOOKUP_SUCCESS = 'conversions-garant-approve-requests/lookup-success';
const LOOKUP_ERROR = 'conversions-garant-approve-requests/lookup-error';

const REQUEST_MANUAL_APPROVE_SUCCESS = 'conversions-garant-approve-requests/request-manual-approve-success';
const REQUEST_MANUAL_APPROVE_ERROR = 'conversions-garant-approve-requests/request-manual-approve-error';

const CLEAR = 'conversions-garant-approve-requests/clear';

const RESTRICT_NEW_APPROVE = 'conversions-garant-approve-requests/restrict-new-approve';

const TOGGLE_COMMENT_POPUP = 'conversions-garant-approve-requests/TOGGLE_COMMENT_POPUP';

// permissions
export const allowedPermissions = {
	view: 'garant_approve_requests_view',
	confirm: 'garant_approve_requests_approve'
};

const filters = createFilters({
	namespace: 'conversionsGarantApproveRequests',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'locations_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		},
		{
			name: 'offer_ids',
			title: 'Offers',
			placeholder: 'Add offer',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'affiliate_id',
			title: 'Affiliates',
			placeholder: 'Add affiliate',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'admin_ids',
			title: 'Admins',
			placeholder: 'Add admin',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS
			}
		}]
});

// initial state
const initialState = {
	data: null,
	isCommentPopupVisible: false,
	commentFormInitialData: {},
	calendarOfCreationDate: calendarOfCreationDate.initialState,
	isUpdateRequestPopupOpened: false,
	manualApproveInitialValues: {
		affiliates: null,
		offers: null,
		countries: null,
		percent_to_approve: null
	},
	table: {
		columns: {
			manager: {
				index: 'manager',
				title: 'Manager/Affiliate/Geo/Offer',
				className: 'fixed-col'
			},
			interval: {
				index: 'interval',
				title: 'Period for Conversions',
				className: 'garant_approve_request_cell min-width-200'
			},
			created_at: {
				index: 'created_at',
				title: 'Created',
				className: 'garant_approve_request_cell min-width-100'
			},
			sid: {
				index: 'sid',
				title: 'Sid',
				className: 'garant_approve_request_cell min-width-100'
			},
			targetApprove: {
				index: 'target_percent',
				title: 'Target percent',
				className: 'garant_approve_request_cell min-width-100'
			},
			toApproveCount: {
				index: 'toApproveCount',
				title: 'Approve leads',
				className: 'garant_approve_request_cell min-width-100'
			},
			pending_count: {
				index: 'pending_count',
				title: 'Pending leads',
				className: 'garant_approve_request_cell min-width-100'
			},
			approve_after_increasing: {
				index: 'approve_after_increasing',
				title: 'Approve after increasing',
				className: 'garant_approve_request_cell min-width-100'
			},
			payout: {
				index: 'payout',
				title: 'Payout',
				className: 'garant_approve_request_cell min-width-100'
			},
			not_trash_count: {
				index: 'not_trash_count',
				title: 'Leads for period',
				className: 'garant_approve_request_cell min-width-100'
			},
			comment: {
				index: 'comment',
				title: 'Comment',
				className: 'garant_approve_request_cell min-width-100'
			},
			offer_margin: {
				index: 'offer_margin',
				title: 'Offer margin*',
				className: 'garant_approve_request_cell min-width-100 garant_approve_request-offer_margin_hint'
			},
			garant_register_start_leads_setting: {
				index: 'garant_register_start_leads_setting',
				title: 'Garant settings',
				className: 'garant_approve_request_cell min-width-100'
			},
			actions: {
				index: 'actions',
				title: 'Actions',
				className: 'fixed-col-right small-coll-100   garant_approve_request_cell min-width-100'
			}
		}
	},
	filters: filters.initialState,
	isRequestForApprovePopupVisible: false,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			if (document.isLoading) {
				return;
			}

			document.isLoading = true;

			return request(getState)(dispatch)
				.then(action => {
					document.isLoading = false;
				});
		},

		locationsLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					const data = action.payload.data
						.filter(entity => !(isRocket && checkIsExcludedCountryCode(entity.country_code)));

					return { options: data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		affiliatesLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		approve: (id) => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			return http({
				url: `${endpoints.ARTIFICIAL_APPROVE_REQUESTS.BASE}/:id/approve`.replace(':id', id),
				method: 'put',
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		decline: (id) => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			return http({
				url: `${endpoints.ARTIFICIAL_APPROVE_REQUESTS.BASE}/:id/decline`.replace(':id', id),
				method: 'put',
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		remove: (id) => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			return http({
				url: `${endpoints.ARTIFICIAL_APPROVE_REQUESTS.BASE}/:id`.replace(':id', id),
				method: 'delete',
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		offersLookup: text => (dispatch, getState) => {
			const hidden_items = _get(getState(), 'root.data.settings.hidden_items');
			const isHiddenRemoved =
				Array.isArray(hidden_items) && hidden_items.includes('conversions-approve-all-offers');

			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?${qs.stringify({ query: text, isShowedRemoved: !isHiddenRemoved })}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		updateComment: (data) => (dispatch, getState) => {
			const id = data && data.id;
			const comment = data && data.comment;
			dispatch(actions.toggleProgressBar());
			return http({
				url: `${endpoints.ARTIFICIAL_APPROVE_REQUESTS.BASE}/:id/comment`.replace(':id', id),
				method: 'put',
				data: { comment },
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
						dispatch(actions.fetchData());
						dispatch(actions.toggleCommentPopup());
					}
				});
		},

		clear: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			dispatch({ type: CLEAR });
			request(getState)(dispatch);
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		toggleCommentPopup: (comment = null, id = null) => ({
			type: TOGGLE_COMMENT_POPUP,
			payload: { comment, id }
		})
	},
	calendarOfCreationDate.actions,
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return {
				...initialState
			};
		},
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data,
			showProgressBar: false,
			error: false
		}),
		[CLEAR]: (state, action) => ({ ...state, initialState }),
		[SET_MONTHLY_PAYMENTS]: (state, action) => ({ ...state, monthlyPayments: action.payload || [] }),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_COMMENT_POPUP]: (state, action) => ({
			...state,
			isCommentPopupVisible: !state.isCommentPopupVisible,
			commentFormInitialData: {...state.commentFormInitialData, ...action.payload }
		}),
		[LOOKUP_ERROR]: state => ({ ...state, showProgressBar: false, error: true }),
		[REQUEST_MANUAL_APPROVE_ERROR]: (state, action) => ({
			...state,
			manualApproveInitialValues: action.meta
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[REQUEST_MANUAL_APPROVE_SUCCESS]: state => ({ ...state, showProgressBar: false }),
		[REQUEST_MANUAL_APPROVE_ERROR]: state => ({ ...state, showProgressBar: false }),
		[RESTRICT_NEW_APPROVE]: state => ({ ...state, showProgressBar: false })
	},
	calendarOfCreationDate.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.conversionsGarantApproveRequests.data,
	getCommentFormInitialData: state => state.conversionsGarantApproveRequests.commentFormInitialData,
	getCalendarOfCreationDate: state =>
		calendarOfCreationDate.selectors.getCalendar(
			state => state.conversionsGarantApproveRequests.calendarOfCreationDate, state => state
		)(state),
	getTable: state => {
		const { table, data } = state.conversionsGarantApproveRequests;
		let rows = state.conversionsGarantApproveRequests.data ? data : [];
		return {
			headers: values(table.columns),
			rows: rows,
			sortedColumn: table.sortByColumn
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.conversionsGarantApproveRequests.filters,
		state => state
	)(state),
	getManualApproveInitialValues: state => state.conversionsGarantApproveRequests.manualApproveInitialValues,
	getIsCommentPopupVisible: state => state.conversionsGarantApproveRequests.isCommentPopupVisible,
	getIntl: state => intl(state),
	getProgressBar: state => state.conversionsGarantApproveRequests.showProgressBar,
	getReasons: state => state.root.lists['manual-approve-reasons'],
	getIsRequestForApprovePopupVisible: state => state.conversionsGarantApproveRequests.isRequestForApprovePopupVisible,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.conversionsGarantApproveRequests.error
};

const request = ( getState, baseUrl = null, isOnStart = false) => {
	const query = {};

	const calendar = selectors.getCalendarOfCreationDate(getState()).value;
	const selectedFilters = selectors.getFilters(getState()).selectedFilters;
	for (const filter of selectedFilters) {
		if (!filter.value || !filter.value[0] || !filter.name) {
			continue;
		}
		query.filters = query.filters || {};
		query.filters[filter.name]  = filter.value.map(val => val.value);
	}
	query.date_range = { start: `${calendar.start}T00:00:00Z`, end: `${calendar.end}T23:59:59Z` };

	if (isOnStart) {
		query.date_range = {
			start: moment.utc()
				.date(1).hours(0).minutes(0).seconds(0).milliseconds(0)
				.format('YYYY-MM-DDTHH:mm:ssZ'),
			end: moment.utc()
				.date(1).hours(0).minutes(0).seconds(0).milliseconds(0)
				.add('1', 'month')
				.date(0).hours(23).minutes(59).seconds(59).milliseconds(999)
				.format('YYYY-MM-DDTHH:mm:ssZ')
		};
	}

	return http({
		url: `${endpoints.ARTIFICIAL_APPROVE_REQUESTS.BASE}?${qs.stringify(query)}`,
		method: 'get',
		actions: [ FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/conversions-garant-approve-requests/index.js